import { forwardRef } from 'react';
import { Loader } from 'lucide-react';

// ============================================================================
// Button — the one button to rule them all.
//
// Variants:
//   primary  → brand gradient + drop-shadow + lift on hover (use for CTAs)
//   secondary → outline + accent-tinted background (default action)
//   ghost    → transparent + text colour (tertiary / inline action)
//   danger   → red gradient + matching shadow (destructive)
//   success  → green gradient + matching shadow (positive primary)
//   long     → green gradient — semantic alias for trade direction
//   short    → red gradient   — semantic alias for trade direction
//
// Sizes: xs / sm / md / lg — driven by padding + font-size pairs.
// loading=true shows a spinner + locks the click; disabled greys out without
// the spinner. icon = lucide-react node, placed at the start unless
// iconRight is true.
// ============================================================================

const VARIANT = {
  primary:   { bg: 'var(--grad-accent)',  color: '#fff',                shadow: '0 4px 14px rgba(91,106,240,0.32)',  hoverShadow: '0 8px 22px rgba(91,106,240,0.45)',  border: 'none' },
  secondary: { bg: 'var(--accent-dim)',   color: 'var(--accent-soft)',  shadow: 'none',                                hoverShadow: 'none',                               border: '1px solid var(--border-strong)' },
  ghost:     { bg: 'transparent',         color: 'var(--text-secondary)', shadow: 'none',                              hoverShadow: 'none',                               border: '1px solid var(--border)',
                hoverBg: 'var(--bg-elevated)' },
  danger:    { bg: 'var(--grad-danger)',  color: '#fff',                shadow: '0 4px 14px rgba(239,68,68,0.32)',    hoverShadow: '0 8px 22px rgba(239,68,68,0.45)',    border: 'none' },
  success:   { bg: 'var(--grad-success)', color: '#fff',                shadow: '0 4px 14px rgba(34,197,94,0.32)',    hoverShadow: '0 8px 22px rgba(34,197,94,0.45)',    border: 'none' },
  long:      { bg: 'var(--grad-success)', color: '#fff',                shadow: '0 4px 14px rgba(34,197,94,0.32)',    hoverShadow: '0 8px 22px rgba(34,197,94,0.45)',    border: 'none' },
  short:     { bg: 'var(--grad-danger)',  color: '#fff',                shadow: '0 4px 14px rgba(239,68,68,0.32)',    hoverShadow: '0 8px 22px rgba(239,68,68,0.45)',    border: 'none' },
};

const SIZE = {
  xs: { padX: 8,  padY: 4,  font: 10, gap: 4, radius: 'var(--r-sm)',  iconSize: 11 },
  sm: { padX: 12, padY: 6,  font: 11, gap: 5, radius: 'var(--r-sm)',  iconSize: 12 },
  md: { padX: 16, padY: 9,  font: 12, gap: 6, radius: 'var(--r-md)',  iconSize: 13 },
  lg: { padX: 22, padY: 12, font: 14, gap: 7, radius: 'var(--r-md)',  iconSize: 16 },
};

const Button = forwardRef(function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  disabled = false,
  icon = null,
  iconRight = false,
  fullWidth = false,
  children,
  style = {},
  onClick,
  type = 'button',
  ...rest
}, ref) {
  const v = VARIANT[variant] || VARIANT.secondary;
  const s = SIZE[size] || SIZE.md;
  const isDisabled = disabled || loading;
  const Icon = loading ? Loader : icon;

  return (
    <button
      ref={ref}
      type={type}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: s.gap,
        padding: `${s.padY}px ${s.padX}px`,
        borderRadius: s.radius,
        border: v.border,
        background: isDisabled ? 'var(--bg-elevated)' : v.bg,
        color:      isDisabled ? 'var(--text-muted)'  : v.color,
        fontSize: s.font, fontWeight: 600, fontFamily: 'inherit',
        cursor: isDisabled ? (loading ? 'wait' : 'not-allowed') : 'pointer',
        boxShadow: isDisabled ? 'none' : v.shadow,
        opacity: disabled && !loading ? 0.55 : 1,
        letterSpacing: '0.01em',
        width: fullWidth ? '100%' : undefined,
        whiteSpace: 'nowrap',
        transition:
          'background var(--dur-fast) var(--ease-out), ' +
          'color var(--dur-fast) var(--ease-out), ' +
          'transform var(--dur-fast) var(--ease-out), ' +
          'box-shadow var(--dur-fast) var(--ease-out), ' +
          'opacity var(--dur-fast) var(--ease-out)',
        ...style,
      }}
      onMouseEnter={(e) => {
        if (isDisabled) return;
        if (v.hoverShadow && v.hoverShadow !== 'none') {
          e.currentTarget.style.boxShadow = v.hoverShadow;
          e.currentTarget.style.transform = 'translateY(-1px)';
        }
        if (v.hoverBg) e.currentTarget.style.background = v.hoverBg;
      }}
      onMouseLeave={(e) => {
        if (isDisabled) return;
        e.currentTarget.style.boxShadow = v.shadow;
        e.currentTarget.style.transform = 'none';
        if (v.hoverBg) e.currentTarget.style.background = v.bg;
      }}
      {...rest}
    >
      {Icon && !iconRight && <Icon size={s.iconSize} className={loading ? 'animate-spin' : undefined} strokeWidth={2.2} />}
      {children}
      {Icon && iconRight && <Icon size={s.iconSize} className={loading ? 'animate-spin' : undefined} strokeWidth={2.2} />}
    </button>
  );
});

export default Button;
